import React from 'react';
import { Link } from 'react-router-dom';
import { PaintBucket, Wrench, Sparkles, Home, Package, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const ServicesPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.pt;

  // Helper function for inline translations
  const getText = (ptText, enText, esText) => {
    if (language === 'es') return esText;
    if (language === 'en') return enText;
    return ptText;
  };

  const services = [
    {
      icon: <PaintBucket size={48} className="text-primary" />,
      title: t.services.painting.title,
      description: t.services.painting.description,
      image: 'https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/t4njjsks_WhatsApp%20Image%202026-02-28%20at%207.54.29%20PM.jpeg',
      details: getText(
        'Oferecemos serviços de pintura profissional:\n\n• Pintura Externa: Fachadas, muros e áreas exteriores com tintas resistentes às condições climáticas.\n\n• Pintura Interna: Quartos, salas, cozinhas e outros espaços interiores com acabamento impecável.\n\nUtilizamos materiais de qualidade e garantimos um trabalho limpo e profissional.',
        'We offer professional painting services:\n\n• External Painting: Facades, walls and outdoor areas with weather-resistant paints.\n\n• Internal Painting: Bedrooms, living rooms, kitchens and other interior spaces with impeccable finishing.\n\nWe use quality materials and guarantee clean and professional work.',
        'Ofrecemos servicios de pintura profesional:\n\n• Pintura Externa: Fachadas, muros y áreas exteriores con pinturas resistentes a las condiciones climáticas.\n\n• Pintura Interna: Habitaciones, salas, cocinas y otros espacios interiores con acabado impecable.\n\nUtilizamos materiales de calidad y garantizamos un trabajo limpio y profesional.'
      )
    },
    {
      icon: <Wrench size={48} className="text-primary" />,
      title: t.services.repairs.title,
      description: t.services.repairs.description,
      image: 'https://customer-assets.emergentagent.com/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/6n4k51zy_Criar_imagem_de_um_homem_fazendo_pequena_reparacao_delpmaspu.png',
      details: getText(
        'Realizamos pequenos reparos domésticos como troca de lâmpadas, instalação de tomadas, reparo de fechaduras e diversos ajustes necessários no seu lar.',
        'We perform small home repairs such as changing light bulbs, installing sockets, repairing locks and various necessary adjustments in your home.',
        'Realizamos pequeñas reparaciones domésticas como cambio de bombillas, instalación de enchufes, reparación de cerraduras y diversos ajustes necesarios en su hogar.'
      )
    },
    {
      icon: <Sparkles size={48} className="text-primary" />,
      title: t.services.cleaning.title,
      description: t.services.cleaning.description,
      image: 'https://customer-assets.emergentagent.com/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/fa2voecp_Criar_imagem_de_uma_mulher_com_o_logo_e_o_uniforme_delpmaspu.png',
      details: getText(
        'Oferecemos serviços completos de limpeza:\n\n• Limpeza Profunda: Limpeza intensiva e detalhada de toda a residência, incluindo áreas de difícil acesso, eletrodomésticos e cantos.\n\n• Limpeza Doméstica: Limpeza regular para manutenção do lar, incluindo aspiração, lavagem de pisos e limpeza de superfícies.\n\nCuidamos de cada detalhe para deixar seu espaço impecável.',
        'We offer complete cleaning services:\n\n• Deep Cleaning: Intensive and detailed cleaning of the entire residence, including hard-to-reach areas, appliances and corners.\n\n• Regular Cleaning: Regular cleaning for home maintenance, including vacuuming, floor washing and surface cleaning.\n\nWe take care of every detail to leave your space impeccable.',
        'Ofrecemos servicios completos de limpieza:\n\n• Limpieza Profunda: Limpieza intensiva y detallada de toda la residencia, incluyendo áreas de difícil acceso, electrodomésticos y rincones.\n\n• Limpieza Doméstica: Limpieza regular para mantenimiento del hogar, incluyendo aspirado, lavado de pisos y limpieza de superficies.\n\nCuidamos cada detalle para dejar su espacio impecable.'
      )
    },
    {
      icon: <Home size={48} className="text-primary" />,
      title: t.services.maintenance.title,
      description: t.services.maintenance.description,
      image: 'https://customer-assets.emergentagent.com/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/6n4k51zy_Criar_imagem_de_um_homem_fazendo_pequena_reparacao_delpmaspu.png',
      details: getText(
        'Manutenção preventiva e corretiva de imóveis. Mantemos sua propriedade em perfeitas condições de uso e conservação.',
        'Preventive and corrective property maintenance. We keep your property in perfect conditions of use and conservation.',
        'Mantenimiento preventivo y correctivo de inmuebles. Mantenemos su propiedad en perfectas condiciones de uso y conservación.'
      )
    },
    {
      icon: <Package size={48} className="text-primary" />,
      title: t.services.furniture.title,
      description: t.services.furniture.description,
      image: 'https://customer-assets.emergentagent.com/job_abcc7bd2-0b3e-4d48-8c0b-718beda2f71c/artifacts/4e9lx8g1_Criar_imagem_de_um_homem_fazendo_uma_montagem_de_m_delpmaspu.png',
      details: getText(
        'Montagem rápida e profissional de móveis de todos os tipos. Economize tempo e evite complicações com nossa equipa especializada.',
        'Fast and professional assembly of all types of furniture. Save time and avoid complications with our specialized team.',
        'Montaje rápido y profesional de muebles de todo tipo. Ahorre tiempo y evite complicaciones con nuestro equipo especializado.'
      )
    }
  ];

  return (
    <div className="min-h-screen" data-testid="services-page">
      <section className="py-16 md:py-24 bg-gradient-to-b from-primary to-primary-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
            {t.servicesPage.title}
          </h1>
          <p className="text-lg md:text-xl font-body max-w-3xl mx-auto">
            {t.servicesPage.subtitle}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-detail-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="mb-4">{service.icon}</div>
                  <h2 className="text-3xl md:text-4xl font-heading font-semibold text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 font-body mb-4">
                    {service.description}
                  </p>
                  <p className="text-base text-gray-700 font-body mb-6">
                    {service.details}
                  </p>
                  <Link
                    to="/ie/quote"
                    data-testid={`service-cta-${index}`}
                    className="inline-flex items-center px-6 py-3 bg-primary text-white font-body font-semibold rounded-md hover:bg-primary-light transition-colors"
                  >
                    {t.servicesPage.cta}
                    <ArrowRight className="ml-2" size={20} />
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50" data-testid="services-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-gray-900 mb-6">
            {getText('Pronto para começar?', 'Ready to get started?', '¿Listo para comenzar?')}
          </h2>
          <p className="text-lg text-gray-600 font-body mb-8">
            {getText(
              'Entre em contacto connosco para solicitar um orçamento personalizado e sem compromisso.',
              'Contact us to request a personalized quote with no obligation.',
              'Contáctenos para solicitar un presupuesto personalizado y sin compromiso.'
            )}
          </p>
          <Link
            to="/ie/quote"
            data-testid="services-bottom-cta"
            className="inline-flex items-center px-8 py-3 bg-primary text-white font-body font-semibold rounded-md hover:bg-primary-light transition-colors shadow-md hover:shadow-lg"
          >
            {t.hero.cta}
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
